// MessageCards — Presentational block that renders the hotel / restaurant
// recommendation cards attached to a chat message.
//
// Extracted verbatim from `AIChatBot.js` (the in-file `MessageCards`
// sub-component) as part of the behavior-preserving refactor (issue #240).
// Render-only, wrapped in React.memo. Imports `./HotelCard` and
// `./RestaurantCard` via the same modules the source used (now reached through
// the relative path from `aiChatBot/presentation/`). The
// `onReservationCreated` → `learnSignal('dining_booking', …)` wiring is an
// event-handler callback fired on user interaction, preserved exactly as the
// source had it (no data-fetching or state at render time).
//
// Preserves the source's empty/fallback render: returns `null` when there are
// no cards, when the card type is unrecognized, or when the items array is
// empty.
//
// Props:
//   cards    - { type: 'hotel' | 'restaurant', items: [...] } | undefined
//   tripData - the trip object, passed through to HotelCard / RestaurantCard
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';
import HotelCard from '../../HotelCard';
import RestaurantCard from '../../RestaurantCard';
import { learnSignal } from '../../../services/tasteProfileService';

// ── Message card rendering ─────────────────────────────────────────────────────
export const MessageCards = React.memo(function MessageCards({ cards, tripData }) {
  if (cards?.type === 'hotel' && cards.items?.length > 0) {
    return (
      <div style={{
        marginTop: '10px',
        maxHeight: '420px',
        overflowY: 'auto',
        paddingRight: '4px'
      }}>
        {cards.items.map((hotel) => (
          <HotelCard key={hotel.id} hotel={hotel} trip={tripData} />
        ))}
      </div>
    );
  }

  if (cards?.type === 'restaurant' && cards.items?.length > 0) {
    return (
      <div style={{
        marginTop: '10px',
        maxHeight: '420px',
        overflowY: 'auto',
        paddingRight: '4px'
      }}>
        {cards.items.map((restaurant) => (
          <RestaurantCard
            key={restaurant.id}
            restaurant={restaurant}
            trip={tripData}
            onReservationCreated={(_reservation) => {
              learnSignal('dining_booking', {
                restaurantName: restaurant.name,
                cuisine: restaurant.cuisine,
                placeId: restaurant.placeId,
                city: restaurant.city,
                priceRange: restaurant.priceRange,
              });
            }}
          />
        ))}
      </div>
    );
  }

  return null;
});

export default MessageCards;
